import { useIntersection } from '@mantine/hooks'
import { ButtonBase } from '@mui/material'
import type { FC, ReactNode, SVGProps } from 'react'
import { memo, useContext, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { match, P } from 'ts-pattern'
import FluentCheckmarkNote20Filled from '~icons/fluent/checkmark-note-20-filled'
import FluentProhibitedNote20Filled from '~icons/fluent/prohibited-note-20-filled'
import FluentWarning24Filled from '~icons/fluent/warning-24-filled'
import IcRoundMarkEmailUnread from '~icons/ic/round-mark-email-unread'
import MdiCrownCircle from '~icons/mdi/crown-circle'
import { useMyInfo } from 'src/services/apollo/auth'
import type { Notification } from 'src/services/apollo/generated/graphql'
import {
  ArtworkType,
  NotificationType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { formatTime } from 'src/utils/dayjs'
import { getThumbnailUrl } from 'src/utils/media'
import { startEventGroup, track } from 'src/utils/tracker'
import { isValidUrl } from 'src/utils/url'
import { getUserDisplayName } from 'src/utils/user'
import { getArtworkTypeLabelKeyForRanking } from '../Ranking/ranking-utils'
import { openShareRankingArtworkDialog } from '../Ranking/RankingArtworkShareDialog'
import Text from '../ui/Text'
import UserAvatar from '../UserAvatar'
import { ackNotification } from './ackNotification'
import {
  appealNotificationTypes,
  highlightedNotificationTypes,
  rankingNotificationTypes,
  systemNotificationTypes,
  useNotificationTabs,
  warningNotificationTypes,
} from './common'
import { NotificationActions } from './NotificationAction'
import { NotificationListContext } from './NotificationListContext'

const getTabTypeForDataTracking = (
  tabValues: ReturnType<typeof useNotificationTabs>,
  itemType: Notification['type'],
) => {
  const types = tabValues.find(tab => tab.types.includes(itemType))
  return types ? types.label : undefined
}

type NotificationItemProps = {
  item: Notification
}
const _NotificationItem: FC<NotificationItemProps> = ({ item }) => {
  const { t } = useTranslation(['common'])
  const { closePanel } = useContext(NotificationListContext)

  const { ref: intersectionRef, entry: intersection } = useIntersection({
    root: document.getElementById('notifications-container'),
    rootMargin: '0px',
    threshold: 1,
  })
  const navigate = useNavigate()
  const { data: me } = useMyInfo()

  const tabValues = useNotificationTabs()

  useEffect(() => {
    if (!item.unread) return
    if (intersection?.isIntersecting) {
      // Ignore fake notification Id
      if (Number.isNaN(Number(item.id))) return
      ackNotification(item.id)
    }
  }, [intersection, item.id, item.unread])

  const {
    relatedUsers,
    artwork,
    createdAt,
    refId,
    refType,
    refTitle,
    type,
    unread,
  } = item

  const users = useMemo(() => {
    if (!relatedUsers) return
    if (relatedUsers.length === 0) return

    const name = getUserDisplayName(relatedUsers[0])
    const firstUserName = (
      <div className="inline-block">
        <Link
          target="_blank"
          to={`/@${relatedUsers[0].username}`}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            window.open(`/@${relatedUsers[0].username}`, '_blank')
          }}
        >
          <span className="text-primary font-bold">{name}</span>
        </Link>
      </div>
    )
    if (relatedUsers.length > 1)
      return (
        <Trans
          i18nKey="common:someone-and-x-others"
          values={{
            x: relatedUsers.length - 1,
            name,
          }}
        >
          {firstUserName}
        </Trans>
      )
    return firstUserName
  }, [relatedUsers])

  const refTitleComp = refTitle && (
    <span className="text-primary font-medium">
      {t('common:punctuations.colon')} {refTitle}
    </span>
  )

  const content = useMemo(() => {
    // Don't treat appeal notifications as notifications with artwork
    if (artwork && !appealNotificationTypes.includes(type)) {
      const typeLabel = getArtworkTypeLabelKeyForRanking(item)
      const notiTypeMsg = match(item)
        .with({ type: NotificationType.Like }, () =>
          match(item)
            .with({ artwork: { type: ArtworkType.ComfyFlow } }, () =>
              t('common:notification.liked-your-workflow'),
            )
            .otherwise(() => t('common:notification.liked-your-artwork')),
        )
        .with({ type: NotificationType.Comment }, () =>
          match(item)
            .with({ artwork: { type: ArtworkType.ComfyFlow } }, () =>
              t('common:notification.comment-your-workflow'),
            )
            .otherwise(() => t('common:notification.comment-your-artwork')),
        )
        .with({ type: NotificationType.CommentReply }, () =>
          t('common:notification.reply-to-your-comment'),
        )
        .with({ type: NotificationType.DailyRanking }, item =>
          t('common:notification.your-artwork-made-ranking', {
            filterType: t('common:ranking.filter-type.daily'),
            type: t(`common:ranking.${typeLabel}.label`),
          }),
        )
        .with({ type: NotificationType.WeeklyRanking }, () =>
          t('common:notification.your-artwork-made-ranking', {
            filterType: t('common:ranking.filter-type.weekly'),
            type: t(`common:ranking.${typeLabel}.label`),
          }),
        )
        .with({ type: NotificationType.MonthlyRanking }, () =>
          t('common:notification.your-artwork-made-ranking', {
            filterType: t('common:ranking.filter-type.monthly'),
            type: t(`common:ranking.${typeLabel}.label`),
          }),
        )
        .with({ type: NotificationType.NewbiesRanking }, () =>
          t('common:notification.your-artwork-made-ranking', {
            filterType: t('common:ranking.filter-type.new-user'),
            type: t(`common:ranking.${typeLabel}.label`),
          }),
        )
        .with({ type: NotificationType.ContestReward }, () => (
          <span>{t('common:notification.contest-reward')}</span>
        ))
        .with({ type: NotificationType.WorldNewCharacter }, () => (
          <span className="text-primary-light">
            <Text
              i18nKey="common:notification.added-character"
              components={{ b: <span className="text-primary font-medium" /> }}
              tOptions={{
                worldName: item.data?.world?.name,
              }}
            />
          </span>
        ))
        .otherwise(() => null)

      const notiContent =
        item.type === NotificationType.Comment ||
        item.type === NotificationType.CommentReply
          ? item.content
          : artwork.title

      return (
        <span className="text-primary-light">
          {users} {notiTypeMsg}
          {notiContent ? (
            <span className="text-primary">
              {t('common:punctuations.colon')} {notiContent}
            </span>
          ) : null}
        </span>
      )
    }

    return (
      match(item)
        .returnType<ReactNode>()
        .with({ type: NotificationType.Follow }, () => (
          <span>
            {users} {t('common:notification.followed-you')}
          </span>
        ))
        .with({ type: NotificationType.ModelClaim }, () => (
          <span className="">
            <span className="mr-1">🎊</span>
            {t('common:notification.claimed-model')}
            {refTitleComp}
          </span>
        ))
        .with({ type: NotificationType.ModelClaimedByOthers }, () => (
          <span>
            {t('common:notification.model-claimed-by-others', {
              modelName: refTitle ?? 'unknown',
            })}
          </span>
        ))
        .with(
          { type: NotificationType.ItemLike, refType: RefType.Model },
          () => (
            <span className="text-primary-light">
              {users} {t('common:notification.liked-your-model')}
              {refTitleComp}
            </span>
          ),
        )
        .with({ type: NotificationType.ItemLike }, () => (
          <span className="text-primary-light">
            {users} {t('common:notification.liked-your-unknown-item')}
            {refTitleComp}
          </span>
        ))
        .with({ type: NotificationType.TrainingTaskCompleted }, () => (
          <span>
            {t('common:notification.your-training-task-is-completed')}
          </span>
        ))
        // HACK: Temporary hack for a mock privilege noti
        // Real privilege noti doesn't have valid content
        .with(
          { type: NotificationType.Privilege, content: P.when(c => !!c) },
          noti => <span>{noti.content}</span>,
        )
        // NOTE: One time special case for relaunch notification
        // display
        .with(
          {
            type: NotificationType.Privilege,
            data: P.when(data => !!data?.memberGift),
          },
          noti => (
            <span>
              {t('common:notification.privilege-relaunch-gift-received', {
                percentage: noti.data!.memberGift!.percentage ?? 50,
              })}
            </span>
          ),
        )
        .with({ type: NotificationType.Privilege }, () => (
          <span>{t('common:notification.privilege-received')}</span>
        ))
        .with({ type: NotificationType.MembershipGifted }, () => (
          <span>{t('common:notification.membership-gifted')}</span>
        ))
        .with({ type: NotificationType.MembershipExpired }, () => (
          <span>{t('common:notification.membership-expired')}</span>
        ))
        .with({ type: NotificationType.MembershipRenewed }, () => (
          <span>{t('common:notification.membership-renewed')}</span>
        ))
        .with({ type: NotificationType.MembershipRenewalFailed }, () => (
          <span>{t('common:notification.membership-renewal-failed')}</span>
        ))
        .with({ type: NotificationType.PaymentFailed }, () => (
          <span>{t('common:notification.payment-failed')}</span>
        ))
        .with({ type: NotificationType.ModelCoverNsfw }, () => (
          <span>{t('common:notification.model-cover-nsfw')}</span>
        ))
        .with({ type: NotificationType.UserAvatarNsfw }, () => (
          <span>{t('common:notification.user-avatar-nsfw')}</span>
        ))
        .with({ type: NotificationType.UserCoverNsfw }, () => (
          <span>{t('common:notification.user-cover-nsfw')}</span>
        ))
        .with({ type: NotificationType.AppealAccepted }, () => (
          <span>
            {t(
              `common:notification.appeal-accepted.${
                item.refType?.toLowerCase() ?? 'unknown'
              }`,
            )}
          </span>
        ))
        .with({ type: NotificationType.AppealRejected }, () => (
          <span>
            {t(
              `common:notification.appeal-rejected.${
                item.refType?.toLowerCase() ?? 'unknown'
              }`,
            )}
          </span>
        ))
        .with({ type: NotificationType.ContestReward }, () => (
          <span>{t('common:notification.contest-reward')}</span>
        ))
        .with({ type: NotificationType.System }, () => (
          <span className="whitespace-break-spaces">{item.content}</span>
        ))
        .otherwise(() => null)
    )
  }, [item, users, artwork, t])

  const link = useMemo(() => {
    return match({
      artworkId: artwork?.id,
      characterId: artwork?.character?.id,
      type: item.type,
      refType,
      refId,
      firstUsername: relatedUsers?.[0]?.username,
      extra: item.extra,
    })
      .returnType<string>()
      .with(
        /**
         * Supports extra.link override for system notification
         */
        {
          type: NotificationType.System,
          extra: P.select({
            link: P.when((url): url is string => {
              // Testing if the link is a valid URL or relative path
              if (typeof url !== 'string') return false
              if (url.startsWith('/')) return true
              return isValidUrl(url)
            }),
          }),
        },
        extra => extra.link,
      )
      .with(
        { type: P.when(type => rankingNotificationTypes.includes(type)) },
        () => '',
      )
      .with(
        { characterId: P.string },
        ({ characterId }) => `/character/${characterId}`,
      )
      .with({ artworkId: P.string }, ({ artworkId }) => `/artwork/${artworkId}`)
      .with(
        { firstUsername: P.string },
        ({ firstUsername }) => `/@${firstUsername}`,
      )
      .with(
        { refType: RefType.Model, refId: P.string },
        ({ refId }) => `/model/${refId}`,
      )
      .with(
        { type: NotificationType.Privilege },
        () => `/@${me?.username}/credits`,
      )
      .with({ type: NotificationType.PaymentFailed }, () => `/me`)
      .with(
        { type: NotificationType.MembershipExpired },
        () => `/membership/plans`,
      )
      .with({ type: NotificationType.MembershipGifted }, () => `/me`)
      .otherwise(() => '')
  }, [artwork?.id, refId, refType, item.type, relatedUsers?.[0]?.username])

  const notiClickHandler = useMemo(() => {
    if (!artwork) return null
    return match({ type: item.type })
      .with(
        { type: P.when(type => rankingNotificationTypes.includes(type)) },
        () => () => {
          const typeLabel = getArtworkTypeLabelKeyForRanking()
          const filterType =
            type === NotificationType.DailyRanking
              ? 'today'
              : type === NotificationType.WeeklyRanking
                ? 'weekly'
                : type === NotificationType.MonthlyRanking
                  ? 'monthly'
                  : 'newbies'

          closePanel()
          openShareRankingArtworkDialog(artwork, {
            rankNumber: item.data?.rank?.rankNumber ?? 1,
            type: typeLabel,
            filterType,
          })
        },
      )
      .otherwise(() => null)
  }, [item.type, artwork?.id])

  if (!content) return <div className="h-px"></div> // Virtuoso doesn't like 0px height element

  const isSystem = systemNotificationTypes.includes(type)
  const isMembershipExpiry = type === NotificationType.MembershipExpired

  // different notification styles
  const notiType = match({ unread, type })
    .returnType<NotificationStyleType>()
    .with(
      { type: P.when(t => warningNotificationTypes.includes(t)) },
      () => 'warning',
    )
    .with(
      { type: P.when(t => highlightedNotificationTypes.includes(t)) },
      () => 'highlight',
    )
    .with({ unread: true }, () => 'unread')
    .otherwise(() => 'normal')
  const notiClassName = notificationStyleClassNames[notiType]

  return (
    <ButtonBase
      className={cx(
        'w-full p-2 mb-1 rounded-2xl flex items-center gap-2 text-sm transition duration-100 group',
        notiClassName,
      )}
      ref={intersectionRef}
      onClick={() => {
        // seperate system noti tracking
        if (isSystem) {
          track('notification_system_content_click', {
            type,
          })
        } else {
          track('notification_content_click', {
            type: getTabTypeForDataTracking(tabValues, type),
          })
        }
        if (isMembershipExpiry) {
          startEventGroup('member_source', 'membership-expired-noti', {
            persistent: true,
          })
        }

        if (link) {
          // Link is a local path, use client-side nav
          if (link.startsWith('/')) {
            navigate(link)
          } else {
            window.open(link, '_blank', 'noopener')
          }
        }
        notiClickHandler?.()
      }}
    >
      <NotificationLeft item={item} />
      <div className="flex-1 no-underline block text-left self-stretch">
        {content}{' '}
        <span className="text-primary-light text-xs whitespace-nowrap block mt-1">
          {formatTime(createdAt)}
        </span>
      </div>
      <NotificationRight item={item} />
    </ButtonBase>
  )
}

const NotificationItem = memo(_NotificationItem)

const avatarAsPreviewTypes: NotificationType[] = [NotificationType.Follow]

const NotificationLeft = (props: { item: Notification }) => {
  const { item } = props

  const { relatedUsers, type } = item

  const isSystem = systemNotificationTypes.includes(type)

  if (isSystem) {
    const icon = getSystemIconMap(type)
    if (!icon) return null
    return (
      <div>
        {icon({
          className:
            'w-8 h-8 text-[32px] opacity-40 group-hover:opacity-80 transition',
        })}
      </div>
    )
  }

  const userAvatar =
    relatedUsers && relatedUsers.length > 0 ? (
      <Link
        to={`/@${relatedUsers[0].username}`}
        target="_blank"
        onClick={() => {
          track('notification_avatar_click')
        }}
        className="shrink-0"
      >
        <UserAvatar user={relatedUsers[0]} className="size-[42px]" />
      </Link>
    ) : (
      <div className="rounded-full h-[42px] w-[42px] bg-slate-500 shrink-0"></div>
    )

  const leftPreview = avatarAsPreviewTypes.includes(type) ? (
    userAvatar
  ) : (
    <MediaPreview item={item} />
  )

  return leftPreview
}

const NotificationRight = (props: { item: Notification }) => {
  const { type } = props.item
  const isSystem = systemNotificationTypes.includes(type)

  if (!isSystem) return <NotificationActions item={props.item} />

  return <MediaPreview item={props.item} />
}

const MediaPreview = (props: { item: Notification }) => {
  const { artwork, refId, refMedia, refType } = props.item

  const mediaLink = (() => {
    if (refType === RefType.Artwork) return `/artwork/${refId}`
    // compatibility with old notifications
    if (artwork != null) return `/artwork/${artwork.id}`

    if (refType === RefType.Model) return `/model/${refId}`
    return ''
  })()

  const mediaPreviewUrl = getThumbnailUrl(refMedia ?? artwork?.media)
  const mediaPreview = mediaPreviewUrl ? (
    <Link
      to={mediaLink}
      target="_blank"
      className="w-[62px] h-[62px] rounded-xl bg-cover bg-center"
      style={{
        backgroundImage: `url(${mediaPreviewUrl})`,
      }}
      title="related artwork image"
      onClick={e => {
        e.stopPropagation()
        track('notification_preview_click')
      }}
    ></Link>
  ) : null
  return mediaPreview
}

function getSystemIconMap(
  type: NotificationType,
): (props: SVGProps<SVGSVGElement>) => ReactNode {
  return match({ type })
    .returnType<(props: SVGProps<SVGSVGElement>) => ReactNode>()
    .when(
      ({ type }) =>
        (
          [
            NotificationType.ModelClaim,
            NotificationType.TrainingTaskCompleted,
            NotificationType.ModelClaimedByOthers,
          ] as NotificationType[]
        ).includes(type),
      () => IcRoundMarkEmailUnread,
    )
    .when(
      ({ type }) =>
        (
          [
            NotificationType.ContestReward,
            NotificationType.MembershipExpired,
            NotificationType.MembershipRenewalFailed,
          ] as NotificationType[]
        ).includes(type),
      () => IcRoundMarkEmailUnread,
    )
    .when(
      ({ type }) =>
        (
          [
            NotificationType.PaymentFailed,
            NotificationType.ModelCoverNsfw,
            NotificationType.UserAvatarNsfw,
            NotificationType.UserCoverNsfw,
          ] as NotificationType[]
        ).includes(type),
      () => FluentWarning24Filled,
    )
    .when(
      ({ type }) =>
        (
          [
            NotificationType.Privilege,
            NotificationType.MembershipGifted,
            NotificationType.MembershipRenewed,
          ] as NotificationType[]
        ).includes(type),
      () => MdiCrownCircle,
    )
    .when(
      ({ type }) =>
        ([NotificationType.AppealAccepted] as NotificationType[]).includes(
          type,
        ),
      () => FluentCheckmarkNote20Filled,
    )
    .when(
      ({ type }) =>
        ([NotificationType.AppealRejected] as NotificationType[]).includes(
          type,
        ),
      () => FluentProhibitedNote20Filled,
    )
    .otherwise(() => () => null)
}

const notificationStyleClassNames = {
  unread: 'bg-hover',
  normal:
    'hover:bg-background-light active:bg-background-light active:brightness-90',
  warning:
    'bg-amber-100 hover:bg-amber-200 dark:bg-amber-700 dark:hover:bg-amber-600',
  highlight:
    'bg-violet-100 hover:bg-violet-200 dark:bg-violet-700 dark:hover:bg-violet-600',
}

type NotificationStyleType = keyof typeof notificationStyleClassNames

export default NotificationItem
